import { EditorView, basicSetup } from 'codemirror';
import { EditorState, type Extension } from '@codemirror/state';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import type { LanguageId } from '../pipeline/types';

export interface CursorInfo {
  line: number; // 0-based
  column: number; // 0-based
}

export interface EditorCallbacks {
  onChange: (path: string, content: string) => void;
  onCursorMove: (path: string, cursor: CursorInfo) => void;
}

function languageExtension(languageId: LanguageId | undefined): Extension[] {
  switch (languageId) {
    case 'javascript':
      return [javascript()];
    case 'typescript':
      return [javascript({ typescript: true })];
    case 'python':
      return [python()];
    default:
      return [];
  }
}

export class CodeEditor {
  private view: EditorView | null = null;
  private container: HTMLElement;
  private callbacks: EditorCallbacks;
  private currentPath: string | null = null;

  constructor(container: HTMLElement, callbacks: EditorCallbacks) {
    this.container = container;
    this.callbacks = callbacks;
    this.renderPlaceholder();
  }

  private renderPlaceholder() {
    // `is-empty` lets the shell collapse the editor to a single line so the chat gets the whole
    // column until a file is actually open; the path drives the editor's header label.
    this.container.classList.add('is-empty');
    this.container.removeAttribute('data-path');
    this.container.innerHTML = '<div class="placeholder">No file open — pick one from the file tree to edit it here.</div>';
  }

  openFile(path: string, content: string, languageId: LanguageId | undefined) {
    this.currentPath = path;
    this.view?.destroy();
    this.container.innerHTML = '';
    this.container.classList.remove('is-empty');
    this.container.setAttribute('data-path', path);

    const updateListener = EditorView.updateListener.of((update) => {
      if (!this.currentPath) return;
      if (update.docChanged) {
        this.callbacks.onChange(this.currentPath, update.state.doc.toString());
      }
      if (update.selectionSet || update.docChanged) {
        const pos = update.state.selection.main.head;
        const line = update.state.doc.lineAt(pos);
        this.callbacks.onCursorMove(this.currentPath, { line: line.number - 1, column: pos - line.from });
      }
    });

    const state = EditorState.create({
      doc: content,
      extensions: [basicSetup, ...languageExtension(languageId), updateListener],
    });
    this.view = new EditorView({ state, parent: this.container });
  }

  getCursor(): CursorInfo | null {
    if (!this.view) return null;
    const pos = this.view.state.selection.main.head;
    const line = this.view.state.doc.lineAt(pos);
    return { line: line.number - 1, column: pos - line.from };
  }

  getSelection(): { startLine: number; endLine: number } | null {
    if (!this.view) return null;
    const sel = this.view.state.selection.main;
    if (sel.empty) return null;
    return {
      startLine: this.view.state.doc.lineAt(sel.from).number - 1,
      endLine: this.view.state.doc.lineAt(sel.to).number - 1,
    };
  }

  getCurrentPath(): string | null {
    return this.currentPath;
  }

  /** Closes whatever's open and shows the placeholder — used when a new project folder replaces the current one. */
  reset() {
    this.currentPath = null;
    this.view?.destroy();
    this.view = null;
    this.renderPlaceholder();
  }

  replaceContent(content: string) {
    if (!this.view) return;
    this.view.dispatch({ changes: { from: 0, to: this.view.state.doc.length, insert: content } });
  }
}
